import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formState, setFormState] = useState('idle'); // idle, loading, success, error
  const [showGuestWarning, setShowGuestWarning] = useState(false);

  // Check for remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async () => {
    // Reset any previous errors
    setError('');
    
    // Validate inputs
    if (!email) {
      setError('Email is required');
      return;
    }
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    setIsLoading(true);
    setFormState('loading');
    
    try {
      // Login with backend
      const response = await authAPI.login(email, password);
      
      // Store token in local storage
      localStorage.setItem('token', response.data.token);
      
      // Store user data
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // If remember me is checked, store email in local storage
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      setFormState('success');
      
      // Small delay for success animation
      setTimeout(() => {
        // Redirect to game
        navigate('/game');
      }, 800);
      
    } catch (error) {
      console.error('Login error:', error);
      setError(
        error.response?.data?.message || 
        'Login failed. Please check your credentials and try again.'
      );
      setFormState('error');
    } finally {
      setIsLoading(false);
    }
  };

   const handleGuestMode = () => {
    // Create a guest user token or use a predefined guest token
    const guestToken = 'guest_' + Math.random().toString(36).substr(2, 9);
    
    // Store guest token
    localStorage.setItem('token', guestToken);
    
    // Store guest user information
    localStorage.setItem('user', JSON.stringify({
      id: 'guest_' + Math.random().toString(36).substr(2, 9),
      username: 'Guest Player',
      isGuest: true
    }));
    
    // Navigate to game
    navigate('/game');
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // Reset form state after error animation completes
  useEffect(() => {
    if (formState === 'error') {
      const timer = setTimeout(() => {
        setFormState('idle');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formState]);

  const buildingBlockVariants = {
    initial: { opacity: 0, y: -20 },
    animate: i => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.03,
      },
    }),
  };

  return (
    <motion.div 
      className="relative w-full flex justify-center items-center bg-gradient-to-b from-slate-900 to-slate-800 overflow-hidden px-4 py-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <motion.div 
        className="absolute bottom-0 left-0 w-full h-2/5 bg-gradient-to-b from-blue-600/10 to-blue-600/30"
        style={{clipPath: 'polygon(0% 60%, 5% 50%, 10% 55%, 15% 40%, 20% 45%, 25% 30%, 30% 40%, 35% 25%, 40% 40%, 45% 20%, 50% 25%, 55% 15%, 60% 35%, 65% 25%, 70% 40%, 75% 20%, 80% 35%, 85% 30%, 90% 45%, 95% 35%, 100% 50%, 100% 100%, 0% 100%)'}}
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
      >
        {/* Animated stars/lights in the skyline */}
        {[...Array(15)].map((_, i) => (
          <motion.div 
            key={i}
            className="absolute w-1 h-1 bg-yellow-400 rounded-full"
            style={{
              left: `${5 + (i * 6)}%`,
              bottom: `${20 + Math.random() * 60}%`,
            }}
            animate={{
              opacity: [0.2, 1, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 1.5 + Math.random() * 2,
              repeat: Infinity,
              repeatType: "reverse",
              delay: Math.random() * 2,
            }}
          />
        ))}
      </motion.div>
      
      {/* Main content */}
      <div className="flex justify-center items-center w-full z-10 p-4">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-md"
        >
          {/* Brand container */}
          <div className="flex flex-col items-center mb-6 md:mb-8">
            {/* Building icon */}
            <a href="/"><img alt="Hotel Hero Logo" class="w-16 h-16 object-contain mr-3" src="/logo.png"/></a>

            {/* Guest Mode Warning Modal */}
            {showGuestWarning && (
              <motion.div 
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div 
                  className="bg-white rounded-2xl p-4 md:p-8 max-w-md w-full shadow-2xl mx-4"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                >
                  <div className="text-center">
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-3 md:mb-4 text-yellow-500" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                      />
                    </svg>
                    <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-gray-800">Guest Mode Limitations</h2>
                    <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
                      Guest mode allows you to play the game, but:
                      <ul className="list-disc list-inside text-left mt-2 space-y-1 md:space-y-2">
                        <li>Progress won't be saved permanently</li>
                        <li>Some features may be restricted</li>
                        <li>You won't have a persistent account</li>
                      </ul>
                    </p>
                    <div className="flex justify-center space-x-3 md:space-x-4">
                      <button
                        onClick={() => setShowGuestWarning(false)}
                        className="px-3 py-1.5 md:px-4 md:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm md:text-base"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          setShowGuestWarning(false);
                          handleGuestMode();
                        }}
                        className="px-3 py-1.5 md:px-4 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm md:text-base"
                      >
                        Continue as Guest
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
            
            {/* Title */}
            <motion.h1 
              className="font-sans text-3xl md:text-4xl font-extrabold text-white mb-1 md:mb-2 drop-shadow"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              Hotel
              <motion.span 
                className="text-orange-500 relative inline-block pb-1 border-b-2 border-orange-500"
                initial={{ width: 0 }}
                animate={{ width: "auto" }}
                transition={{ delay: 0.9, duration: 0.4 }}
              >
                Hero
              </motion.span>
            </motion.h1>
            
            {/* Tagline */}
            <motion.p 
              className="text-white/80 text-sm md:text-base mb-4 md:mb-5 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
            >
              Build Your Empire, Crush Your Rivals
            </motion.p>
          </div>
          
          {/* Login form */}
          <motion.div 
            className="bg-white rounded-2xl p-6 md:p-8 w-full shadow-md"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            variants={{
              error: { x: [0, -10, 10, -10, 10, 0], transition: { duration: 0.5 } }
            }}
          >
            <motion.h2 
              className="text-xl md:text-2xl font-semibold mb-5 md:mb-6 text-slate-800 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              Welcome Back
            </motion.h2>
            
            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div 
                  className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-lg text-xs md:text-sm"
                  initial={{ opacity: 0, height: 0, margin: 0, padding: 0 }}
                  animate={{ opacity: 1, height: "auto", margin: "0 0 1rem", padding: "0.75rem" }}
                  exit={{ opacity: 0, height: 0, margin: 0, padding: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>
            
            <motion.div
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="relative mb-4 md:mb-5"
            >
              <input 
                className="w-full py-2.5 md:py-3 px-4 pl-10 border border-slate-200 rounded-lg text-sm md:text-base bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-400 absolute left-3 top-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </motion.div>

            <motion.div 
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="relative mb-4 md:mb-5"
            >
              <input 
                className="w-full py-2.5 md:py-3 px-4 pl-10 border border-slate-200 rounded-lg text-sm md:text-base bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-400 absolute left-3 top-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <button 
                type="button"
                className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </motion.div>
            
            <motion.div 
              className="flex flex-wrap md:flex-nowrap justify-between items-center mb-5 md:mb-6 text-xs md:text-sm"
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <label className="flex items-center text-slate-500 cursor-pointer group mb-2 md:mb-0">
                <div 
                  className={`w-4 h-4 border border-slate-300 rounded mr-2 flex items-center justify-center transition-colors ${rememberMe ? 'bg-blue-600 border-blue-600' : 'group-hover:border-blue-400'}`}
                  onClick={() => setRememberMe(!rememberMe)}
                >
                  <AnimatePresence>
                    {rememberMe && (
                      <motion.svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-3 w-3 text-white" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ type: "spring", stiffness: 500, damping: 20 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </motion.svg>
                    )}
                  </AnimatePresence>
                </div>
                Remember me
              </label>
              
              <motion.span 
                className="text-blue-600 text-xs md:text-sm cursor-pointer hover:underline relative overflow-hidden group"
                onClick={() => navigate('/forgot-password')}
                whileHover="hover"
              >
                Forgot Password?
                <motion.span
                  className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500"
                  initial={{ scaleX: 0 }}
                  variants={{
                    hover: {
                      scaleX: 1,
                      transition: { duration: 0.3 }
                    }
                  }}
                  style={{ originX: 0 }}
                />
              </motion.span>
            </motion.div>
            
            <motion.button 
              className={`w-full py-2.5 md:py-3 px-5 bg-blue-600 text-white border-none rounded-lg text-sm md:text-base font-medium cursor-pointer text-center hover:bg-blue-700 transition-colors relative overflow-hidden ${isLoading ? 'opacity-90' : ''}`}
              onClick={handleLogin}
              disabled={isLoading}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <AnimatePresence mode="wait">
                {formState === 'loading' ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center"
                  >
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Logging in...
                  </motion.div>
                ) : formState === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center"
                  >
                    <svg className="h-4 md:h-5 w-4 md:w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Success!
                  </motion.div>
                ) : (
                  <motion.span
                    key="login"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    Log In
                  </motion.span>
                )}
              </AnimatePresence>
              
              {/* Button hover effect */}
              <motion.div 
                className="absolute top-0 left-0 right-0 bottom-0 bg-blue-700 z-0"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.4 }}
                style={{ originX: 0 }}
              />
            </motion.button>
            
            <motion.div 
              className="mt-4 text-center text-xs md:text-sm text-slate-500 flex flex-col space-y-2 md:space-y-0 md:flex-row md:justify-center md:items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
            >
              <div className="flex items-center justify-center">
                New to HotelHero? 
                <motion.span 
                  className="text-blue-600 ml-1 hover:underline cursor-pointer relative inline-block group"
                  onClick={() => navigate('/register')}
                  whileHover="hover"
                >
                  Create Account
                  <motion.span
                    className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500"
                    initial={{ scaleX: 0 }}
                    variants={{
                      hover: {
                        scaleX: 1,
                        transition: { duration: 0.3 }
                      }
                    }}
                    style={{ originX: 0 }}
                  />
                </motion.span>
              </div>
              <span className="hidden md:inline mx-2 text-slate-400">|</span>
              <motion.span 
                className="text-green-600 hover:underline cursor-pointer relative inline-block group"
                onClick={() => setShowGuestWarning(true)}
                whileHover="hover"
              >
                Play as Guest
                <motion.span
                  className="absolute bottom-0 left-0 w-full h-0.5 bg-green-500"
                  initial={{ scaleX: 0 }}
                  variants={{
                    hover: {
                      scaleX: 1,
                      transition: { duration: 0.3 }
                    }
                  }}
                  style={{ originX: 0 }}
                />
              </motion.span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
      
      <motion.div 
        className="absolute bottom-2 md:bottom-5 w-full text-center text-white/60 text-xs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
      >
        &copy; 2025 HotelHero. All rights reserved.
      </motion.div>
    </motion.div>
  );
};

export default Login;